import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate, useLocation } from "react-router-dom";
import api from 'Common/Client/Interceptor';
import {
  Card,
  Button,
  Typography,
  Divider,
  List,
  Avatar,
  Tag,
  Space,
  Spin,
  Alert,
  Modal,
  Form,
  Select,
  message,
} from 'antd';
import {
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import API from 'Common/Client/endpoint';
const { Title, Text } = Typography;
const { Option } = Select;
const CheckReviews = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFirstReview, setIsFirstReview] = useState(false);
  const [businessGroupFields, setBusinessGroupFields] = useState([]);
  const [skills, setSkills] = useState([]);
  const [form] = Form.useForm();
  const [loadingFields, setLoadingFields] = useState(false);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [employee, setEmployee] = useState({
    id: null,
    name: '',
    businessGroup: '',
    field: null,
    manager: '',
    lastReview: null,
    isReviewAllowed: false,
    nextEligibleReviewDate: null,
    businessGroupId: null
  });
  const navigate = useNavigate();
  const location = useLocation();
  const learnerId = location.state?.learnerId;
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/PerformanceEvaluation/Learner/Performance-Reviews?learnerId=${learnerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = res.data;
        setEmployee({
          id: data.learnerId,
          name: data.learnerName,
          businessGroup: data.businessGroupName,
          field: data.businessGroupFieldName,
          manager: data.managerName,
          lastReview: data.lastReviewDate,
          isReviewAllowed: data.isReviewAllowed,
          nextEligibleReviewDate: data.nextEligibleReviewDate,
          businessGroupId: data.businessGroupId
        });
        setReviews(data.performanceReviews || []);
        setIsFirstReview(data.performanceReviews?.length === 0);
        setIsButtonDisabled(!data.isReviewAllowed);
      } catch (error) {
        console.error("Failed to fetch performance reviews", error);
        setError("Failed to load performance reviews");
      } finally {
        setLoading(false);
      }
    };
    if (learnerId) {
      fetchReviews();
    } else {
      setError("No employee selected");
      setLoading(false);
    }
  }, [learnerId]);
  const fetchBusinessGroupFields = async () => {
    try {
      setLoadingFields(true);
      const token = localStorage.getItem("token");
      const response = await api.get(API.businessGroup.getManagerDetail);
      if (response.data && response.data.fields) {
        setBusinessGroupFields(response.data.fields.map(field => ({
          businessGroupFieldId: field.id,
          businessGroupFieldName: field.name
        })));
      } else {
        throw new Error("No fields data received");
      }
    } catch (error) {
      console.error("Failed to fetch business group fields", error);
      message.error("Failed to load business group fields");
      throw error;
    } finally {
      setLoadingFields(false);
    }
  };
  const fetchSkills = async (fieldId) => {
    try {
      setLoadingSkills(true);
      const token = localStorage.getItem("token");
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/Skills/businessgroup/${employee.businessGroupId}?fieldId=${fieldId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const skillsData = response.data || [];
      setSkills(skillsData.map(skill => ({
        skillId: skill.id,
        skillName: skill.name
      })));
    } catch (error) {
      console.error("Failed to fetch skills", error);
      message.error("Failed to load skills");
    } finally {
      setLoadingSkills(false);
    }
  };
  const handleFieldChange = (fieldId) => {
    form.setFieldsValue({ skills: [] });
    if (fieldId) {
      fetchSkills(fieldId);
    }
  };
  const handleStartReview = async () => {
    if (!employee) {
      message.warning("Employee data is still loading");
      return;
    }
    if (isFirstReview && (!employee.field || employee.field === null)) {
      try {
        await fetchBusinessGroupFields();
        setIsModalVisible(true);
      } catch (error) {
        message.error("Failed to load required data");
      }
    } else {
      navigateToReviewForm();
    }
  };
  const closeModal = () => {
    if (isModalVisible) {
      setIsModalVisible(false);
      form.resetFields();
    }
  };
  const handleModalSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      const token = localStorage.getItem("token");
      const existingSkills = values.skills.filter(skill => typeof skill === 'number');
      const newSkills = values.skills.filter(skill => typeof skill === 'string');
      const payload = {
        learnerId: employee.id,
        businessGroupFieldId: values.fieldId,
        skills: existingSkills,
        customSkills: newSkills
      };
      const response = await api.put(
        '${process.env.REACT_APP_API_URL}/ManagerLearners/UpdateLearnerCareerProfile',
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
      if (response.status === 200) {
        message.success("Employee profile updated successfully");
        closeModal();
        setTimeout(() => {
          navigateToReviewForm();
        }, 300);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      message.error(error.response?.data || "Update failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const navigateToReviewForm = () => {
    navigate("/performance-review", {
      state: {
        learnerId: employee?.id,
        firstName: employee?.name?.split(" ")[0],
        lastName: employee?.name?.split(" ")[1],
      },
    });
  };
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(parseISO(dateString), 'PP');
  };
  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarFilled key={`full-${i}`} style={{ color: '#faad14' }} />);
    }
    if (hasHalfStar) {
      stars.push(<StarFilled key="half" style={{ color: '#faad14', opacity: 0.5 }} />);
    }
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<StarOutlined key={`empty-${i}`} style={{ color: '#faad14' }} />);
    }
    return <Space>{stars}</Space>;
  };
  const getRatingColor = (ratingCode) => {
    switch (ratingCode) {
      case 'FM': return 'red';
      case 'ME': return 'orange';
      case 'EE': return 'green';
      case 'AE': return 'blue';
      default: return 'gray';
    }
  };
  if (loading) return <Spin size="large" tip="Loading performance reviews..." />;
  if (error) return <Alert type="error" message={error} showIcon />;
  return (
    <Card
      className="card-professional"
      title={`Performance Reviews: ${employee?.name}`}
      loading={loading}
      bordered
      extra={
        <Button type="default" onClick={() => navigate(-1)}>
          Back
        </Button>
      }
    >
      <div className="card mb-4 border-primary">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="card-title mb-1">New Performance Review</h5>
              {!employee?.isReviewAllowed && employee?.nextEligibleReviewDate && (
                <p className="mb-0 text-muted">
                  Next review available on <strong>{formatDate(employee.nextEligibleReviewDate)}</strong>
                  <br />
                  <small>Performance reviews can be conducted every 6 months</small>
                </p>
              )}
            </div>
            <button
              onClick={handleStartReview}
              className="btn btn-primary"
              disabled={isButtonDisabled || !employee?.isReviewAllowed}
            >
              Start Review
            </button>
          </div>
        </div>
      </div>
      <Divider orientation="left">
        <Title level={4} style={{ margin: 0 }}>Review History</Title>
      </Divider>
      {reviews.length > 0 ? (
        <List
          itemLayout="vertical"
          dataSource={reviews}
          renderItem={(review, index) => (
            <List.Item
              key={review.id}
              extra={
                <Button
                  type="primary"
                  onClick={() =>
                    navigate("/performance-review-report", {
                      state: {
                        learnerId: review.learnerId,
                        reviewId: review.id,
                        reviewData: review
                      },
                    })
                  }
                  className="btn btn-sm btn-outline-primary align-self-start"
                >
                  View Full Report
                </Button>
              }
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    style={{
                      backgroundColor: index % 2 === 0 ? '#1890ff' : '#52c41a',
                      verticalAlign: 'middle'
                    }}
                  >
                    {reviews.length - index}
                  </Avatar>
                }
                title={
                  <Space>
                    <Text strong>
                      {formatDate(review.periodStart)} - {formatDate(review.periodEnd)}
                    </Text>
                    <Tag color={getRatingColor(review.performanceRatingCode)}>
                      {review.performanceRatingName}
                    </Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small">
                    <div>
                      <Text strong>Overall Rating: </Text>
                      {renderRating(review.rating)}
                      <Text style={{ marginLeft: 8 }}>({review.totalPerformanceScore.toFixed(1)}/100)</Text>
                    </div>
                    <div>
                      <Text strong>Accountability: </Text>
                      <Text>{review.overallAccountabilityGoalRating.toFixed(1)}/5</Text>
                    </div>
                    <div>
                      <Text strong>Performance Factors: </Text>
                      <Text>{review.overallPerformanceFactorRating.toFixed(1)}/5</Text>
                    </div>
                    <div>
                      <Text strong>Dynamic Organization: </Text>
                      <Text>{review.overallDynamicOrganizationRating.toFixed(1)}/5</Text>
                    </div>
                    <Text type="secondary">
                      Reviewed by {review.managerName} on {formatDate(review.createdOn)}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Card>
          <Text type="secondary">No performance reviews found for this employee</Text>
        </Card>
      )}
      <Modal
        title="First-Time Review Setup"
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={() => setIsModalVisible(false)}
        okText="Continue to Review"
        cancelText="Cancel"
        confirmLoading={loadingFields || loadingSkills || isSubmitting}
        okButtonProps={{
          disabled: loadingFields || loadingSkills || isSubmitting || selectedSkills.length < 5,
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="fieldId"
            label="Business Group Field"
            rules={[{ required: true, message: 'Please select a field' }]}
          >
            <Select
              placeholder={loadingFields ? "Loading fields..." : "Select a field"}
              loading={loadingFields}
              onChange={handleFieldChange}
              disabled={loadingFields}
              notFoundContent={loadingFields ? "Loading..." : "No fields available"}
            >
              {businessGroupFields.map(field => (
                <Option
                  key={field.businessGroupFieldId}
                  value={field.businessGroupFieldId}
                >
                  {field.businessGroupFieldName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="skills"
            label="Skills"
            rules={[
              { required: true, message: 'Please select at least five skills' },
              {
                validator: (_, value) => {
                  if (value && value.length >= 5) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Please select at least 5 skills'));
                },
              },
            ]}          >
            <Select
              mode="tags"
              placeholder={loadingSkills ? "Loading skills..." : "Select skills or type to add new.But at least 5 skills are required"}
              loading={loadingSkills}
              disabled={!form.getFieldValue('fieldId') || loadingSkills}
              value={selectedSkills} // controlled value
              onChange={(value) => {
                setSelectedSkills(value); // track skills in state
                form.setFieldsValue({ skills: value });
              }}
              onInputKeyDown={(e) => {
                if (e.key === "Backspace" && !e.target.value) {
                  // Prevent AntD default remove
                  e.stopPropagation();
                  // Optional: remove last skill manually
                  setSelectedSkills((prev) => {
                    const newSkills = prev.slice(0, -1);
                    form.setFieldsValue({ skills: newSkills });
                    return newSkills;
                  });
                }
              }}
              tokenSeparators={[',']}
            >
              {skills.map(skill => (
                <Option key={skill.skillId} value={skill.skillId}>
                  {skill.skillName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
export default CheckReviews;